import React, { useState } from 'react';
import Table from '../../components/common/Table';
import { FileText } from 'lucide-react';
import MandateListDrawer from '../drawers/MandateListDrawer';
import { BatchFileLogClient, BatchFileLogDTO, SearchRequestDTO_batchFileLogSearchRequestDTO } from '../../services/apiClient';
import { API_BASE_URL } from '../../config/config';
import axiosInstance from '../../services/axiosInstance';

interface MemberTableProps {
  data: any[];
  handleEdit: (service: any) => void;
  handleDelete: (service: any) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
}

const MemberTable: React.FC<MemberTableProps> = ({
  data,
  handleEdit,
  handleDelete,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
}) => {
  const [mandates, setMandates] = useState<BatchFileLogDTO[]>([]);
  const [selectedMember, setSelectedMember] = useState<{ id: string; name: string } | null>(null);
  const [isLoadingMandates, setIsLoadingMandates] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const batchFileLogClient = new BatchFileLogClient(API_BASE_URL, axiosInstance);

  const handleViewMandate = async (member: any) => {
    if (!member.id) {
      alert('Invalid member data.');
      return;
    }

    setIsLoadingMandates(true);
    try {
      const searchRequest: SearchRequestDTO_batchFileLogSearchRequestDTO = {
        filters: {
          memberId: member.id
        },
        page: 1,
        pageSize: 100, // Get all mandates for the member
        sortBy: 'CreatedUtc',
        sortDescending: true
      };

      const response = await batchFileLogClient.searchBatchFileLogs(searchRequest);

      if (response.success && response.data?.results) {
        const mandateList = response.data.results as BatchFileLogDTO[];

        if (mandateList.length === 0) {
          alert('No mandates found for this member.');
          return;
        }

        setMandates(mandateList);
        setSelectedMember({
          id: member.id,
          name: `${member.firstName} ${member.lastName}`.trim()
        });
        setIsDrawerOpen(true);
      } else {
        alert('Failed to load mandates. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching mandates:', error);
      alert('Failed to load mandates. Please try again.');
    } finally {
      setIsLoadingMandates(false);
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedMember(null);
    setMandates([]);
  };

  return (
    <>
      <Table
        columns={[
          {
            label: 'First Name',
            key: 'firstName',
          },
          {
            label: 'Last Name',
            key: 'lastName',
          },
          {
            label: 'Email',
            key: 'email',
          },
          {
            label: 'Phone',
            key: 'phone',
          },
          {
            label: 'Debit Order',
            key: 'isDebitOrder',
            render: (row) => (
              row.isDebitOrder ? (
                <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                  Yes
                </span>
              ) : (
                <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                  No
                </span>
              )
            ),
          },
        ]}
        data={data}
        actions={{
          onEdit: handleEdit,
          onDelete: handleDelete,
          extra: [
            {
              label: 'View mandates',
              icon: <FileText />,
              onClick: handleViewMandate,
              visible: (row) => row.isDebitOrder === true
            }
          ]
        }}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
      />
      {selectedMember && (
        <MandateListDrawer
          memberName={selectedMember.name}
          mandates={mandates}
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
        />
      )}
    </>
  );
};

export default MemberTable;
